import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AuthShell } from "@/components/auth/AuthShell";
import styles from "@/components/auth/AuthForm.module.scss";
import { getAuthErrorMessage } from "@/components/auth/auth-helpers";
import { useAppAuth } from "@/components/auth/AuthProvider";

export function SignUpPage() {
  const {
    isLoaded,
    startSignUp,
    verifySignUp,
    resendSignUpCode,
  } = useAppAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"details" | "verify">("details");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSignUpSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isLoaded) return;

    setIsSubmitting(true);
    setError("");
    setNotice("");

    try {
      const result = await startSignUp(email, password);
      if (result === "complete") {
        window.location.assign("/app");
        return;
      }
      setStep("verify");
      setNotice(`Enter the code sent to ${email.trim()}.`);
    } catch (submitError) {
      setError(getAuthErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifySubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isLoaded) return;

    setIsSubmitting(true);
    setError("");

    try {
      await verifySignUp(code.trim());
      window.location.assign("/app");
    } catch (submitError) {
      setError(getAuthErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendCode = async () => {
    if (!isLoaded) return;

    setIsSubmitting(true);
    setError("");

    try {
      await resendSignUpCode();
      setNotice(`A fresh code was sent to ${email.trim()}.`);
    } catch (submitError) {
      setError(getAuthErrorMessage(submitError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AuthShell mode="signup">
      <div className={styles.stack}>
        {notice ? <p className={styles.notice}>{notice}</p> : null}
        {error ? <p className={styles.error}>{error}</p> : null}

        {step === "details" ? (
          <form className={styles.form} onSubmit={handleSignUpSubmit}>
            <label className={styles.field}>
              <span className={styles.label}>Email</span>
              <input
                className={styles.input}
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder="you@example.com"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                disabled={!isLoaded || isSubmitting}
                required
              />
            </label>

            <label className={styles.field}>
              <span className={styles.label}>Password</span>
              <input
                className={styles.input}
                type="password"
                autoComplete="new-password"
                placeholder="Create a password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={!isLoaded || isSubmitting}
                required
              />
            </label>

            <button className={styles.submit} type="submit" disabled={!isLoaded || isSubmitting}>
              {isSubmitting ? (
                <span className={styles.spinner}>
                  <Loader2 size={20} className={styles.spinIcon} />
                </span>
              ) : (
                "Create account"
              )}
            </button>
          </form>
        ) : (
          <form className={styles.form} onSubmit={handleVerifySubmit}>
            <p className={styles.assistive}>Finish setup with the code from your email.</p>

            <label className={styles.field}>
              <span className={styles.label}>Verification code</span>
              <input
                className={styles.input}
                type="text"
                autoComplete="one-time-code"
                inputMode="numeric"
                placeholder="6-digit code"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                disabled={!isLoaded || isSubmitting}
                required
              />
            </label>

            <button className={styles.submit} type="submit" disabled={!isLoaded || isSubmitting}>
              {isSubmitting ? (
                <span className={styles.spinner}>
                  <Loader2 size={20} className={styles.spinIcon} />
                </span>
              ) : (
                "Verify and continue"
              )}
            </button>

            <div className={styles.splitActions}>
              <button
                className={styles.ghostButton}
                type="button"
                onClick={handleResendCode}
                disabled={!isLoaded || isSubmitting}
              >
                Resend code
              </button>

              <button
                className={styles.ghostButton}
                type="button"
                onClick={() => {
                  setStep("details");
                  setCode("");
                  setError("");
                  setNotice("");
                }}
                disabled={isSubmitting}
              >
                Edit details
              </button>
            </div>
          </form>
        )}

        <div className={styles.linkRow}>
          <span>Already have an account?</span>
          <Link className={styles.link} to="/login">
            Sign in
          </Link>
        </div>
      </div>
    </AuthShell>
  );
}
